import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Container,
  Text,
  Select,
  HStack,
  Spinner,
  Center,
  Flex,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  VStack,
  useToast,
  Input,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { SearchIcon, DownloadIcon } from "@chakra-ui/icons";
import { supabase } from "../lib/supabaseClient";

// 현재 년월을 YYYYMM 형식으로 반환 (7일 이전에는 이전달)
const getCurrentYearMonth = () => {
  const date = new Date();
  const currentDay = date.getDate();

  // 7일 이전인 경우 이전달로 설정
  if (currentDay < 7) {
    const prevMonth = new Date(date.getFullYear(), date.getMonth() - 1, 1);
    return `${prevMonth.getFullYear()}${String(
      prevMonth.getMonth() + 1
    ).padStart(2, "0")}`;
  }

  return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}`;
};

const ReferralSettlement = () => {
  const [selectedDate, setSelectedDate] = useState(getCurrentYearMonth());
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const toast = useToast();

  // 년월 옵션 생성 (현재 월부터 과거 24개월)
  const getDateOptions = useCallback(() => {
    const options = [];
    const currentDate = new Date();
    const currentDay = currentDate.getDate();

    // 현재 월이 7일 이후인 경우에만 현재 월 포함
    const startMonth = currentDay >= 7 ? 0 : 1;

    for (let i = startMonth; i < 24; i++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - i,
        1
      );
      const yearMonth = `${date.getFullYear()}${String(
        date.getMonth() + 1
      ).padStart(2, "0")}`;
      options.push(yearMonth);
    }
    return options;
  }, []);

  // 소수점 8자리에서 반올림하는 함수
  const roundToEightDecimals = useCallback((num) => {
    return Math.round(num * 100000000) / 100000000;
  }, []);

  // 추천인 유저 정보 조회
  const fetchReferralUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc(
        "get_user_info_by_referral_tidokang"
      );

      if (error) throw error;

      // 각 유저별로 정산 데이터 조회
      const usersWithSettlements = await Promise.all(
        (data || []).map(async (user) => {
          try {
            // 일반 정산 금액 조회 (global 제외)
            const { data: normalData, error: normalError } = await supabase.rpc(
              "calculate_total_settlements",
              {
                target_email: user.email,
                target_month: selectedDate,
              }
            );

            if (normalError) throw normalError;

            // global 정산 금액 조회
            const { data: globalData, error: globalError } = await supabase.rpc(
              "global_only_settlement",
              {
                target_email: user.email,
                target_month: selectedDate,
              }
            );

            if (globalError) throw globalError;

            // 정산 가능 금액 계산
            const totalSettlement = roundToEightDecimals(
              (normalData[0]?.total_sum || 0) + (globalData || 0)
            );

            // 총 수익 계산 (정산 가능 금액 / 0.9)
            const totalRevenue =
              totalSettlement > 0
                ? roundToEightDecimals(totalSettlement / 0.9)
                : 0;

            // 플플 수수료 금액 계산 (총 수익 * commission_rate / 100)
            const commissionRate = parseFloat(user.commission_rate) || 0;
            const plplCommission = roundToEightDecimals(
              totalRevenue * (commissionRate / 100)
            );

            // 추천인 수수료 금액 계산 (플플 수수료 금액 / 2)
            const referralCommission = roundToEightDecimals(plplCommission / 2);

            return {
              ...user,
              totalSettlement,
              totalRevenue,
              commissionRate,
              plplCommission,
              referralCommission,
            };
          } catch (error) {
            console.error(
              `Error fetching settlement for ${user.email}:`,
              error
            );
            return {
              ...user,
              totalSettlement: 0,
              totalRevenue: 0,
              commissionRate: parseFloat(user.commission_rate) || 0,
              plplCommission: 0,
              referralCommission: 0,
            };
          }
        })
      );

      setUsers(usersWithSettlements);
    } catch (error) {
      console.error("Error fetching referral users:", error);
      toast({
        title: "추천인 유저 정보 조회 실패",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setLoading(false);
    }
  }, [selectedDate, roundToEightDecimals, toast]);

  // CSV 파일 다운로드
  const downloadCSV = useCallback(() => {
    if (users.length === 0) return;

    const headers = [
      "이메일",
      "가입일",
      "사용자 정산 금액(원)",
      "총 정산 금액(원)",
      "수수료율(%)",
      "플플 수수료 금액(원)",
      "추천인 수수료 금액(원)",
    ];

    const csvContent = [
      headers.join(","),
      ...users.map((user) =>
        [
          user.email,
          user.created_at
            ? new Date(user.created_at).toISOString().slice(0, 10)
            : "",
          Math.round(user.totalSettlement),
          Math.round(user.totalRevenue),
          user.commissionRate,
          Math.round(user.plplCommission),
          Math.round(user.referralCommission),
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `추천인정산_${selectedDate}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "CSV 파일 다운로드 완료",
      status: "success",
      duration: 2000,
      isClosable: true,
      position: "top",
    });
  }, [users, selectedDate, toast]);

  // 검색 필터링된 유저 목록
  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users;
    return users.filter((user) =>
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [users, searchTerm]);

  // useEffect로 데이터 로딩
  useEffect(() => {
    fetchReferralUsers();
  }, [fetchReferralUsers]);

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={6} align="stretch">
        {/* 제목 */}
        <Box textAlign="center">
          <Text fontSize="3xl" fontWeight="bold" color="blue.600">
            추천인 정산 관리
          </Text>
          <Text fontSize="md" color="gray.600" mt={2}>
            Tido Kang 추천인을 통한 가입 유저들의 정산 금액을 확인하고 추천인
            수수료 금액을 확인합니다.
          </Text>
        </Box>

        {/* 컨트롤 패널 */}
        <Box bg="white" p={6} borderRadius="xl" boxShadow="md">
          <HStack spacing={6} justify="space-between" flexWrap="wrap">
            <HStack spacing={4}>
              <Text fontWeight="bold">정산월</Text>
              <Select
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                width="200px"
              >
                {getDateOptions().map((date) => (
                  <option key={date} value={date}>
                    {date.slice(0, 4)}-{date.slice(4)}
                  </option>
                ))}
              </Select>
            </HStack>

            <HStack spacing={4}>
              <InputGroup width="300px">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="이메일로 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>

              <Button
                colorScheme="blue"
                leftIcon={<DownloadIcon />}
                onClick={downloadCSV}
                isDisabled={users.length === 0}
              >
                CSV 다운로드
              </Button>
            </HStack>
          </HStack>
        </Box>

        {/* 데이터 테이블 */}
        {loading ? (
          <Center py={20}>
            <VStack spacing={4}>
              <Spinner size="xl" color="blue.500" />
              <Text color="gray.600">데이터를 불러오는 중...</Text>
            </VStack>
          </Center>
        ) : (
          <Box bg="white" borderRadius="xl" boxShadow="md" overflow="hidden">
            <Box p={6} borderBottom="1px" borderColor="gray.200">
              <Text fontSize="lg" fontWeight="bold">
                총 {filteredUsers.length}명의 유저
              </Text>
            </Box>

            <Box overflowX="auto">
              <Table variant="simple" size="md">
                <Thead bg="gray.50">
                  <Tr>
                    <Th textAlign="center">이메일</Th>
                    <Th textAlign="center">가입일</Th>
                    <Th textAlign="center">사용자 정산 금액(원)</Th>
                    <Th textAlign="center">총 정산 금액(원)</Th>
                    <Th textAlign="center">수수료율(%)</Th>
                    <Th textAlign="center">플플 수수료 금액(원)</Th>
                    <Th textAlign="center">추천인 수수료 금액(원)</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredUsers.length === 0 ? (
                    <Tr>
                      <Td colSpan={7} textAlign="center" py={10}>
                        <Text color="gray.500">데이터가 없습니다.</Text>
                      </Td>
                    </Tr>
                  ) : (
                    filteredUsers.map((user, index) => (
                      <Tr
                        key={`${user.email}-${index}`}
                        _hover={{ bg: "blue.50" }}
                      >
                        <Td fontWeight="medium">{user.email}</Td>
                        <Td textAlign="center">
                          {user.created_at
                            ? new Date(user.created_at)
                                .toISOString()
                                .slice(0, 10)
                            : "-"}
                        </Td>
                        <Td textAlign="right" fontWeight="bold">
                          {Math.round(user.totalSettlement).toLocaleString()}
                        </Td>
                        <Td
                          textAlign="right"
                          color="blue.600"
                          fontWeight="bold"
                        >
                          {Math.round(user.totalRevenue).toLocaleString()}
                        </Td>
                        <Td textAlign="center">{user.commissionRate}%</Td>
                        <Td
                          textAlign="right"
                          color="green.600"
                          fontWeight="bold"
                        >
                          {Math.round(user.plplCommission).toLocaleString()}
                        </Td>
                        <Td
                          textAlign="right"
                          color="purple.600"
                          fontWeight="bold"
                        >
                          {Math.round(user.referralCommission).toLocaleString()}
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>
          </Box>
        )}

        {/* 요약 정보 */}
        {!loading && users.length > 0 && (
          <Box bg="blue.50" p={6} borderRadius="xl" boxShadow="md">
            <VStack spacing={4}>
              <Text fontSize="xl" fontWeight="bold" color="blue.700">
                정산 요약 정보
              </Text>
              <Flex justify="space-around" flexWrap="wrap" gap={4}>
                <Box textAlign="center">
                  <Text fontSize="sm" color="gray.600">
                    총 유저 수
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                    {users.length}명
                  </Text>
                </Box>
                <Box textAlign="center">
                  <Text fontSize="sm" color="gray.600">
                    총 추천인 수수료
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="purple.600">
                    {Math.round(
                      users.reduce(
                        (sum, user) => sum + user.referralCommission,
                        0
                      )
                    ).toLocaleString()}
                    원
                  </Text>
                </Box>
              </Flex>
            </VStack>
          </Box>
        )}
      </VStack>
    </Container>
  );
};

export default ReferralSettlement;
